import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthPage from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import AdminLeads from "./pages/AdminLeads";
import Dashboard from "./pages/Dashboard";
import StockDetail from "./pages/StockDetail";
import Compare from "./pages/Compare";
import Demo from "./pages/Demo";
import Portfolio from "./pages/Portfolio";
import Landing from "./pages/Landing";
import Horizons from "./pages/Horizons";
import Metals from "./pages/Metals";
import Land from "./pages/Land";
import "@/App.css";

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone-50">
        <div className="text-stone_brand-light font-mono text-sm uppercase tracking-widest">
          Loading…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (requireAdmin && user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone-50">
        <div className="max-w-md text-center px-6">
          <div className="overline mb-3 text-terracotta">— Restricted</div>
          <div className="font-serif-display text-2xl text-stone_brand-dark mb-2">
            Admin access only
          </div>
          <div className="text-sm text-stone_brand-light">
            You need admin permissions to view this page.
          </div>
        </div>
      </div>
    );
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/signup" element={<AuthPage mode="signup" />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock/:ticker"
        element={
          <ProtectedRoute>
            <StockDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare"
        element={
          <ProtectedRoute>
            <Compare />
          </ProtectedRoute>
        }
      />
      <Route
        path="/demo"
        element={
          <ProtectedRoute>
            <Demo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <Portfolio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/horizons"
        element={
          <ProtectedRoute>
            <Horizons />
          </ProtectedRoute>
        }
      />
      <Route
        path="/metals"
        element={
          <ProtectedRoute>
            <Metals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/land"
        element={
          <ProtectedRoute>
            <Land />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
